import styled from '@emotion/styled';

/**
 * Sub-section inside a unified card (e.g. the Device card's Details /
 * Connectivity / Health / Telemetry blocks). Visually separates groups
 * without forcing each into its own card.
 *
 * The first <Subsection> in a card has no top divider; subsequent ones
 * get a 1px hairline + 20px padding-top.
 *
 * Usage:
 *   <DetailCard>
 *     <h2>Device</h2>
 *     <Subsection>
 *       <SubsectionTitle>Details</SubsectionTitle>
 *       <dl>...</dl>
 *     </Subsection>
 *     <Subsection>
 *       <SubsectionTitle>Connectivity</SubsectionTitle>
 *       ...
 *     </Subsection>
 *   </DetailCard>
 */
export const Subsection = styled.section`
  & + & {
    margin-top: ${({ theme }) => theme.spacing(5)};
    padding-top: ${({ theme }) => theme.spacing(5)};
    border-top: 1px solid ${({ theme }) => theme.border.color.medium};
  }
`;

export const SubsectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.font.roles.subsection.size};
  font-weight: ${({ theme }) => theme.font.roles.subsection.weight};
  letter-spacing: ${({ theme }) => theme.font.roles.subsection.tracking};
  text-transform: ${({ theme }) => theme.font.roles.subsection.transform};
  color: ${({ theme }) => theme.font.color.tertiary};
  line-height: ${({ theme }) => theme.font.roles.subsection.lineHeight};
  margin: 0 0 ${({ theme }) => theme.spacing(3)};
`;

/**
 * SpaceBetween-style row used at the top of a Subsection when the title
 * has a sibling control on the right (e.g. window selector pill group).
 * The internal margin between this row and the body of the subsection
 * is built in.
 */
export const SubsectionRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;
